<template>
  <Transition name="bar">
    <aside
      v-if="visible"
      class="mobile-buy-bar"
      role="region"
      :aria-label="ctaText"
    >
      <div class="mobile-buy-bar__meta">
        <span v-if="entryText" class="mobile-buy-bar__entry text-body-3 text-bold">
          {{ entryText }}
        </span>
        <span v-if="aprText" class="mobile-buy-bar__apr text-body-3">
          · {{ aprText }}
        </span>
      </div>
      <button
        type="button"
        class="mobile-buy-bar__cta"
        @click="onClick"
      >
        {{ ctaText }}
        <span aria-hidden="true">→</span>
      </button>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { formatPercentage } from "~/utilities/helpers/format-data/number";
import { redirectByProviderName } from "~/utilities/helpers/strings/redirectByProviderName";
import { buildReferralRedirectUrl } from "~/utilities/helpers/strings/buildReferralRedirectUrl";
import { BASE_CURRENCY, HUGE_CURRENCY } from "~/composables/CURRENCIES";
import type { UnitResponseDTO } from "~/services/swagger/Api";

interface IProps {
  propertyInfo: UnitResponseDTO | null | undefined;
}

const props = defineProps<IProps>();
const { t } = useI18n();
const { isMobile } = useDevice();

const scrolled = ref(false);
const footerVisible = ref(false);
let footerObserver: IntersectionObserver | null = null;

const entryText = computed(() => {
  const minUSD = props.propertyInfo?.minimumInvestmentUSD;
  if (typeof minUSD !== "number" || minUSD <= 0) return "";
  const base = BASE_CURRENCY.value;
  const nativeCurrency = props.propertyInfo?.currency;
  const nativeMin = props.propertyInfo?.minimumInvestment;
  const value =
    nativeCurrency === base.code &&
    typeof nativeMin === "number" &&
    nativeMin > 0
      ? nativeMin
      : minUSD * base.rate;
  const formattedMin =
    HUGE_CURRENCY.includes(base.code) && value >= 10000
      ? `${base.symbol}${Math.round(value).toLocaleString("en-US")}`
      : value < 1
        ? `${base.symbol}${value.toFixed(2)}`
        : `${base.symbol}${Math.round(value).toLocaleString("en-US")}`;
  return t("unit_page.un_from_short", { amount: formattedMin });
});

const aprText = computed(() => {
  const apr = props.propertyInfo?.apr;
  if (typeof apr !== "number" || apr <= 0) return "";
  return `${formatPercentage(apr)}% APR`;
});

const hasExternalTarget = computed(
  () =>
    !!props.propertyInfo?.referralLink?.link ||
    !!props.propertyInfo?.externalUrl ||
    !!props.propertyInfo?.provider?.url
);

const ctaText = computed(() => {
  if (hasExternalTarget.value) return t("listing_card.buy_tokens");
  return t("listing_card.invest");
});

const visible = computed(() => {
  if (!isMobile) return false;
  if (!props.propertyInfo) return false;
  if (footerVisible.value) return false;
  return scrolled.value;
});

const onClick = () => {
  if (!props.propertyInfo) return;
  redirectByProviderName({
    providerName: props.propertyInfo?.provider?.name,
    providerLink: props.propertyInfo?.provider?.url,
    utmLink:
      buildReferralRedirectUrl(props.propertyInfo?.referral) ||
      props.propertyInfo?.referralLink?.link,
    externalUrl: props.propertyInfo?.externalUrl,
    source: "unit__open_ref__ready_to_invest",
    images: props.propertyInfo?.files,
    logoFile: props.propertyInfo?.provider?.logoFile?.url,
  });
};

const SCROLL_TRIGGER_PX = 300;
const onScroll = () => {
  scrolled.value = window.scrollY > SCROLL_TRIGGER_PX;
};

onMounted(() => {
  if (typeof window === "undefined") return;
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  const footerEl = document.querySelector(".base-footer");
  if (footerEl && "IntersectionObserver" in window) {
    footerObserver = new IntersectionObserver(
      ([entry]) => {
        footerVisible.value = entry.isIntersecting;
      },
      { rootMargin: "0px 0px 0px 0px", threshold: 0 }
    );
    footerObserver.observe(footerEl);
  }
});

onBeforeUnmount(() => {
  if (typeof window === "undefined") return;
  window.removeEventListener("scroll", onScroll);
  footerObserver?.disconnect();
  footerObserver = null;
});
</script>

<style scoped lang="scss">
.mobile-buy-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 900;
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom)) 16px;
  background: var(--surface);
  border-top: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-overlay);

  &__meta {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  &__entry {
    color: var(--text-primary);
  }

  &__apr {
    color: var(--text-secondary);
  }

  &__cta {
    @include ds-primary;
    height: 44px;
    padding: 0 18px;
    border-radius: var(--radius-pill);
    font-weight: var(--fw-bold);
    font-size: var(--font-small);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

@media (max-width: 48rem) {
  .mobile-buy-bar {
    display: flex;
  }
}

.bar-enter-active,
.bar-leave-active {
  transition: transform 0.25s ease, opacity 0.2s ease;
}
.bar-enter-from,
.bar-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
